import { groupLens, LensGroup } from "@/modules/timeline";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { ItemListItemKind, ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { liveQuery, Subscription } from "dexie";
import { EventContext } from "@/domains/metrics/context";
import { DateTime } from "luxon";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { SyncModelKind } from "@/store/sync/types";
import { IndexedAudioRecordingSyncUpdateValue } from "@/store/audio-recordings";
import { itemsModule } from "@/modules/items";

const DEFAULT_LIMIT = 50;

type AudioRecordingListEntry = {
  id: string;
  locallyCreatedAt: string;
  locallyModifiedAt: string;
};

export class AudioRecordingsListPageStore extends AppSubStore {
  supportsAddToCollectionListAction = false;
  supportsMoveNoteToTrashListAction = false;
  supportsMoveToTrashListAction = false;
  listState: ListStateObservable;

  isLoading = true;
  subscriptionInitialized = false;
  liveQuerySubscription?: Subscription;
  recordings: AudioRecordingListEntry[] = [];

  eventContext = EventContext.Unknown;

  private limit = DEFAULT_LIMIT;
  private currentScrollTop = 0;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.listState = new ListStateObservable({
      ...injectedDeps,
      listStateProvider: this,
      canSelect: false,
    });

    makeObservable<
      this,
      | "runLiveQuery"
      | "limit"
      | "currentScrollTop"
      | "toListEntry"
      | "mapRecordingToRow"
      | "resolveDate"
    >(this, {
      toListEntry: false,
      mapRecordingToRow: false,
      resolveDate: false,
      supportsAddToCollectionListAction: true,
      supportsMoveNoteToTrashListAction: true,
      supportsMoveToTrashListAction: true,
      listState: true,
      eventContext: true,
      initialize: true,
      useEffects: true,
      isLoading: observable,
      subscriptionInitialized: observable,
      liveQuerySubscription: observable,
      recordings: observable,
      limit: observable,
      currentScrollTop: observable,
      groupedRecordings: computed,
      limitedRecordings: computed,
      itemRows: computed,
      orderedItemIds: computed,
      hasNoItems: computed,
      canLoadMore: computed,
      scrollTop: computed,
      runLiveQuery: action,
      increaseLimit: action,
      setScrollTop: action,
      handleCreateRecording: action,
      handleOpenSessionManager: action,
    });

    reaction(
      () => this.subscriptionInitialized,
      initialized => {
        if (initialized) {
          setTimeout(() => runInAction(() => (this.isLoading = false)), 100);
        }
      }
    );
  }

  initialize() {
    this.limit = DEFAULT_LIMIT;
  }

  get groupedRecordings(): LensGroup<AudioRecordingListEntry>[] {
    return groupLens(
      this.limitedRecordings.map(recording => ({
        dateTime: this.resolveDate(recording.locallyModifiedAt)?.toJSDate() ?? new Date(),
        item: recording,
      }))
    );
  }

  get limitedRecordings(): AudioRecordingListEntry[] {
    return this.recordings.slice(0, this.limit);
  }

  get itemRows(): ItemListRowData[] {
    const rows: ItemListRowData[] = [];

    for (const group of this.groupedRecordings) {
      rows.push({
        type: ItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      for (const entry of group.items) {
        rows.push(this.mapRecordingToRow(entry.item));
      }
    }

    if (rows.length) {
      rows.push({
        type: ItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }

    return rows;
  }

  get orderedItemIds(): string[] {
    return this.limitedRecordings.map(recording => recording.id);
  }

  get hasNoItems(): boolean {
    return !this.isLoading && this.recordings.length === 0;
  }

  get canLoadMore(): boolean {
    return this.recordings.length > this.limit;
  }

  get scrollTop(): number {
    return this.currentScrollTop;
  }

  useEffects() {
    useEffectOnMount(() => {
      const subscription = this.runLiveQuery();
      return () => subscription?.unsubscribe();
    });

    this.listState.useListStateEffects();
  }

  increaseLimit = () => {
    this.limit += DEFAULT_LIMIT;
  };

  setScrollTop = (value: number) => {
    this.currentScrollTop = value;
  };

  handleCreateRecording = () => {
    this.store.navigation.goToAudioRecordingCreate();
  };

  handleOpenSessionManager = () => {
    this.store.navigation.goToAudioRecordingActiveState({ openInSameTab: true });
  };

  private runLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();

    this.liveQuerySubscription = liveQuery(() =>
      this.store.memDb.mappedTables[SyncModelKind.AudioRecording].local.toArray()
    ).subscribe({
      next: rows => {
        runInAction(() => {
          this.recordings = (rows as IndexedAudioRecordingSyncUpdateValue[])
            .map(row => this.toListEntry(row))
            .sort(
              (a, b) =>
                DateTime.fromISO(b.locallyModifiedAt).toMillis() -
                DateTime.fromISO(a.locallyModifiedAt).toMillis()
            );
          this.subscriptionInitialized = true;
        });
      },
    });

    return this.liveQuerySubscription;
  }

  private toListEntry(row: IndexedAudioRecordingSyncUpdateValue): AudioRecordingListEntry {
    const modifiedAt = row.model_data.locally_modified_at || row.model_data.locally_created_at;

    return {
      id: row.model_id,
      locallyCreatedAt: row.model_data.locally_created_at,
      locallyModifiedAt: modifiedAt,
    };
  }

  private mapRecordingToRow(recording: AudioRecordingListEntry): ItemListRowData {
    const modifiedDate = this.resolveDate(recording.locallyModifiedAt);
    const relative = modifiedDate?.toRelative();
    const subtitle = relative ? `Recording from ${relative}` : "Recording";
    const itemAppearance = itemsModule.resolveAppearance({
      itemId: recording.id,
      appStore: this.store,
    });

    return {
      type: ItemListRowType.Item,
      key: recording.id,
      payload: {
        id: recording.id,
        kind: ItemListItemKind.AudioRecording,
        label: "Audio recording",
        onClick: () => this.store.navigation.goToAudioRecording({ recordingId: recording.id }),
        listState: this.listState,
        appearance: itemAppearance,
        extraRow: {
          id: "status-row-content",
          content: () => (
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              {subtitle}
            </MDSText>
          ),
        },
      },
    };
  }

  private resolveDate(value?: string) {
    if (!value) return undefined;
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt : undefined;
  }
}
